/**
 * Supabase client and auth-session persistence.
 *
 * The application is authenticated-only. Missing credentials therefore render
 * a setup error instead of silently creating an anonymous/local account.
 *
 * Supabase supports a custom storage adapter. We use one so the sign-in screen
 * can offer a real "keep me signed in" choice: persistent sessions use
 * localStorage, while private-device/session-only sign-ins use sessionStorage.
 */
import { createClient } from '@supabase/supabase-js';

const url = process.env.REACT_APP_SUPABASE_URL;
const anonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

const AUTH_PERSISTENCE_KEY = 'prayer-guide-auth-persistence-v1';
const AUTH_STORAGE_KEY = 'prayer-guide-auth-session-v1';

const browserStorage = (kind) => {
    if (typeof window === 'undefined') return null;
    return kind === 'local' ? window.localStorage : window.sessionStorage;
};

export const getRememberMePreference = () => {
    const preference = browserStorage('local')?.getItem(AUTH_PERSISTENCE_KEY);
    return preference !== 'session';
};

export const setRememberMePreference = (rememberMe) => {
    const local = browserStorage('local');
    const session = browserStorage('session');
    if (!local || !session) return;

    local.setItem(AUTH_PERSISTENCE_KEY, rememberMe ? 'local' : 'session');
    const source = rememberMe ? session : local;
    const destination = rememberMe ? local : session;
    const existingSession = source.getItem(AUTH_STORAGE_KEY);
    if (existingSession) destination.setItem(AUTH_STORAGE_KEY, existingSession);
    source.removeItem(AUTH_STORAGE_KEY);
};

const authStorage = {
    getItem(key) {
        return (getRememberMePreference() ? browserStorage('local') : browserStorage('session'))?.getItem(key) ?? null;
    },
    setItem(key, value) {
        const primary = getRememberMePreference() ? browserStorage('local') : browserStorage('session');
        const secondary = getRememberMePreference() ? browserStorage('session') : browserStorage('local');
        primary?.setItem(key, value);
        secondary?.removeItem(key);
    },
    removeItem(key) {
        browserStorage('local')?.removeItem(key);
        browserStorage('session')?.removeItem(key);
    }
};

export const isSupabaseConfigured = Boolean(url && anonKey);

export const supabase = isSupabaseConfigured
    ? createClient(url, anonKey, {
        auth: {
            persistSession: true,
            autoRefreshToken: true,
            detectSessionInUrl: true,
            flowType: 'pkce',
            storage: authStorage,
            storageKey: AUTH_STORAGE_KEY
        }
    })
    : null;
